import React from 'react';
import PropTypes from 'prop-types';
import { ErrorMessage, getIn } from 'formik';
import { FormattedMessage, injectIntl } from 'react-intl';
import InputErrorMessage from './InputErrorMessage';

import s from './RadioGroupInput.module.scss';
import InfoBubble from '../InfoBubble/InfoBubble';

function renderRadioButton(
  field,
  value,
  label,
  infoText,
  className,
  additionalChangeAction,
  required,
  isError,
  errorMsgClassName,
  intl
) {
  const id = `${field.name}_${value}`;
  return (
    <div className={className} key={id}>
      {/*  eslint-disable-next-line jsx-a11y/role-supports-aria-props */}
      <input
        type="radio"
        id={id}
        name={field.name}
        value={value}
        onChange={
          additionalChangeAction
            ? (...args) => {
                additionalChangeAction(...args);
                field.onChange(...args);
              }
            : field.onChange
        }
        checked={field.value === value}
        required={required}
        aria-invalid={isError}
        aria-describedby={isError ? errorMsgClassName : null}
      />
      <div className={s.checkmark} />
      <label htmlFor={id} className={s.label}>
        {label}
        <span className={s.invisible}>{infoText}</span>
      </label>
      {infoText && (
        <div className={s.infoBubble}>
          <InfoBubble
            iconAltText={`${intl.formatMessage({
              id: 'radioGroupInput.infoBubble',
            })} ${label}`}
          >
            {infoText}
          </InfoBubble>
        </div>
      )}
    </div>
  );
}

function RadioGroupInput(props) {
  const {
    field,
    options,
    captionMsg,
    radioButtonsWrapperClass,
    form,
    renderCondition,
    additionalChangeAction,
    required,
    intl,
  } = props;

  if (renderCondition) {
    if (!renderCondition(form.values)) {
      return null;
    }
  }

  const errorMsgClassName = `${field.name}-errorMsg`;

  let isError = false;
  const {
    form: { touched },
  } = props;
  const {
    form: { errors },
  } = props;
  const touchedTemp = getIn(touched, field.name);
  const errorsTemp = getIn(errors, field.name);
  if (touchedTemp && errorsTemp) {
    isError = true;
  }

  const buttons = options.map((o) => {
    const { value, labelMsg, labelText, infoText } = o;
    let label;
    if (labelMsg) {
      label = <FormattedMessage id={labelMsg} />;
    } else {
      label = labelText;
    }
    return renderRadioButton(
      field,
      value,
      label,
      infoText,
      s.radioButton,
      additionalChangeAction,
      required,
      isError,
      errorMsgClassName,
      intl
    );
  });

  return (
    <div className={s.radioGroupWrapper}>
      <fieldset tabIndex={-1} aria-invalid={isError} id={field.name}>
        <legend className={s.groupLabel}>
          <FormattedMessage id={captionMsg} />
          {required && '*'}
        </legend>
        <div className={radioButtonsWrapperClass}>{buttons}</div>
        <div id={errorMsgClassName}>
          <ErrorMessage name={field.name} component={InputErrorMessage} />
        </div>
      </fieldset>
    </div>
  );
}

RadioGroupInput.defaultProps = {
  radioButtonsWrapperClass: null,
  renderCondition: null,
  additionalChangeAction: null,
  required: false,
};

RadioGroupInput.propTypes = {
  /** formik field */
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    value: PropTypes.string,
  }).isRequired,
  /** formik form */
  form: PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    values: PropTypes.object.isRequired,
    // eslint-disable-next-line react/forbid-prop-types
    touched: PropTypes.object,
    // eslint-disable-next-line react/forbid-prop-types
    errors: PropTypes.object,
  }).isRequired,
  /** options array to generate the radio options */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      /** the value it sets in the formik field */
      value: PropTypes.string.isRequired,
      /** label as text */
      labelText: PropTypes.string,
      /** label as react intl message */
      labelMsg: PropTypes.string,
      /** text for an info bubble next to the option */
      infoText: PropTypes.string,
    })
  ).isRequired,
  /** caption message */
  captionMsg: PropTypes.string.isRequired,
  /** radio buttons wrapper class (for grid support) */
  radioButtonsWrapperClass: PropTypes.string,
  renderCondition: PropTypes.func,
  additionalChangeAction: PropTypes.func,
  required: PropTypes.bool,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
};

export default injectIntl(RadioGroupInput);
